import React, { useState } from "react";
import {
  BarChart3,
  TrendingUp,
  Type,
  Image,
  Link2,
  Table,
  ChevronRight,
  Package,
} from "lucide-react";
import { CustomWidgetLibraryModal } from "./CustomWidgetLibraryModal";

interface WidgetLibrarySidebarProps {
  onWidgetDrop: (widgetType: string, x?: number, y?: number) => void;
  onCollapse: () => void;
  onDragStart?: (widgetType: string, w: number, h: number) => void;
  onDragEnd?: () => void;
  customWidgets?: Array<{
    id: string;
    icon: React.ComponentType<any>;
    label: string;
    tooltip: string;
    w: number;
    h: number;
    module?: string;
  }>;
}

export function WidgetLibrarySidebar({
  onWidgetDrop,
  onCollapse,
  onDragStart: _onDragStart,
  onDragEnd: _onDragEnd,
  customWidgets = [],
}: WidgetLibrarySidebarProps) {
  const [isCustomLibraryOpen, setIsCustomLibraryOpen] = useState(false);

  const widgets = [
    {
      id: "chart",
      icon: BarChart3,
      label: "Chart",
      tooltip: "Line, column, bar, pie, and area charts",
      w: 6,
      h: 8,
    },
    {
      id: "metric",
      icon: TrendingUp,
      label: "Metric",
      tooltip: "Display KPIs like revenue or headcount",
      w: 3,
      h: 4,
    },
    {
      id: "text",
      icon: Type,
      label: "Text",
      tooltip: "Add instructions or context",
      w: 4,
      h: 3,
    },
    {
      id: "table",
      icon: Table,
      label: "Table",
      tooltip: "Display data in sortable table",
      w: 6,
      h: 6,
    },
    {
      id: "media",
      icon: Image,
      label: "Media",
      tooltip: "Add images or videos",
      w: 4,
      h: 4,
    },
    {
      id: "iframe",
      icon: Link2,
      label: "Embed",
      tooltip: "Embed external dashboards",
      w: 8,
      h: 6,
    },
  ];

  // Simple click to add widget (finds next available position)
  const handleClick = (widgetType: string) => {
    onWidgetDrop(widgetType); // No position - will auto-find
  };

  return (
    <div className="w-72 bg-gray-50 dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 p-4 flex flex-col h-full">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          Widget Library
        </h3>
        <button
          onClick={onCollapse}
          className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          title="Collapse sidebar"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      <div className="space-y-2 overflow-auto">
        {widgets.map((widget) => {
          const Icon = widget.icon;
          return (
            <div
              key={widget.id}
              onClick={() => handleClick(widget.id)}
              className="w-full flex items-center gap-3 px-4 py-3 bg-white dark:bg-gray-800 hover:bg-blue-50 dark:hover:bg-blue-900/20 border border-gray-200 dark:border-gray-700 rounded-lg transition-all group cursor-pointer hover:scale-105"
              title={widget.tooltip}
            >
              <Icon
                size={20}
                className="text-gray-600 dark:text-gray-400 group-hover:text-blue-600"
              />
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {widget.label}
              </span>
            </div>
          );
        })}
      </div>

      {/* Custom Widget Library Button */}
      <button
        onClick={() => setIsCustomLibraryOpen(true)}
        className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-all flex-shrink-0"
      >
        <Package size={20} />
        Custom Widgets {customWidgets.length > 0 && `(${customWidgets.length})`}
      </button>

      {/* Custom Widget Library Modal */}
      <CustomWidgetLibraryModal
        isOpen={isCustomLibraryOpen}
        onClose={() => setIsCustomLibraryOpen(false)}
        customWidgets={customWidgets}
        onSelectWidget={handleClick}
      />
    </div>
  );
}
